import selectn from "selectn";
import uuid from "uuid/v4";

import createAsyncAction from "utils/createAsyncAction";

import { saveForm } from "editor/actions/status";
import { hydrateForm } from "editor/actions/form";
import { setMessage } from "actions/message";
import { MESSAGES } from "config/constants";

export const LOCK_ITEM_REQUESTED = "LOCK_ITEM_REQUESTED";
export const LOCK_ITEM_SUCCEEDED = "LOCK_ITEM_SUCCEEDED";
export const LOCK_ITEM_FAILED = "LOCK_ITEM_FAILED";

export const lockItem = (id, params) => (dispatch, getState, getClient) => {
  const {
    editor: {
      status: {
        editorType: { name },
      },
    },
  } = getState();

  return createAsyncAction(
    name,
    "lock",
    "LOCK_ITEM",
    args => args,
    error => {
      throw error;
    }
  )(id, params)(dispatch, getState, getClient);
};

export const UNLOCK_ITEM_REQUESTED = "UNLOCK_ITEM_REQUESTED";
export const UNLOCK_ITEM_SUCCEEDED = "UNLOCK_ITEM_SUCCEEDED";
export const UNLOCK_ITEM_FAILED = "UNLOCK_ITEM_FAILED";

export const unlockItem = id => (dispatch, getState, getClient) => {
  const {
    editor: {
      status: {
        editorType: { name },
      },
    },
  } = getState();

  return createAsyncAction(name, "unlock", "UNLOCK_ITEM")(id)(
    dispatch,
    getState,
    getClient
  );
};

export const RESET_LOCK_STATE = "RESET_LOCK_STATE";

export const resetLockState = () => ({ type: "RESET_LOCK_STATE" });

export const CHECK_ITEM_LOCK_REQUESTED = "CHECK_ITEM_LOCK_REQUESTED";
export const CHECK_ITEM_LOCK_SUCCEEDED = "CHECK_ITEM_LOCK_SUCCEEDED";
export const CHECK_ITEM_LOCK_FAILED = "CHECK_ITEM_LOCK_FAILED";

export const checkItemLock = id => (dispatch, getState, getClient) => {
  const {
    editor: {
      status: {
        editorType: { name },
        editKey,
      },
    },
  } = getState();

  return createAsyncAction(name, "checkLock", "CHECK_ITEM_LOCK")(id, editKey)(
    dispatch,
    getState,
    getClient
  );
};

/**
 * Call the unlock method on the API.
 */
export const unlockAndClose = () => (dispatch, getState, getClient) => {
  const {
    editor: {
      form: { id },
      status: { lock, editorType },
    },
  } = getState();
  const client = getClient(dispatch, getState);

  if (lock && lock.locked_by_this_session) {
    client[editorType.name].unlock(id);
  }
};

/**
 * Save the form and unlock it asynchronously.
 */
export const saveAndUnlockForm = () => (dispatch, getState) => {
  const {
    editor: {
      form: { id },
      status: { isDirty, lock },
    },
  } = getState();

  // Forms which have not been saved yet should not be saved on back button.
  // They especially should not be unlocked, because that is impossible.
  if (id && lock && lock.locked_by_this_session) {
    const saveOrNot = () =>
      isDirty ? dispatch(saveForm()) : Promise.resolve();

    return saveOrNot().then(() => {
      return dispatch(unlockItem(id));
    });
  } else {
    return Promise.resolve();
  }
};

export const hydrateAndLockItem = (id, isNew = false) => (
  dispatch,
  getState
) => {
  const state = getState();
  const clientHeadRev = selectn("editor.form.head_rev", state);
  const serverHeadRev = selectn(
    "editor.status.lock.locked_item.head_rev",
    state
  );
  const isCurrent = clientHeadRev && clientHeadRev === serverHeadRev;

  const hydrateOrNot = () =>
    isCurrent || isNew ? Promise.resolve() : dispatch(hydrateForm(id));

  return hydrateOrNot().then(() => {
    const state = getState();
    const clientHeadRev = selectn("editor.form.head_rev", state);
    const editKey = uuid();
    dispatch(setEditKey(editKey));
    return dispatch(
      lockItem(id, { head_rev: clientHeadRev, edit_key: editKey })
    ).catch(() => {
      dispatch(
        setMessage({
          type: MESSAGES.failure,
          message:
            "Something went wrong. Please refresh the page and try again.",
        })
      );
    });
  });
};

export const SET_EDIT_KEY = "SET_EDIT_KEY";

export const setEditKey = editKey => ({ type: SET_EDIT_KEY, payload: editKey });
